import React, { useState } from 'react';
import { Calculator, Check, Send, Sparkles, Loader2 } from 'lucide-react';
import { submitWeb3Form } from '../../services/web3Forms';
import { Card, Button, Input, Badge } from '../ui';

export const ProposalEstimatorWidget: React.FC = () => {
  const [selectedPillars, setSelectedPillars] = useState<string[]>(['Development Services']);
  const [timeline, setTimeline] = useState<string>('Standard (1-2 Months)');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    fullName: '',
  });

  const pillarsList = [
    'Development Services',
    'Marketing & Brand Support',
    'Lead Generation & Sales Support',
    'Admin & Operation Support',
  ];

  const timelines = ['Fast-Track (2-4 Weeks)', 'Standard (1-2 Months)', 'Enterprise (3-6 Months)'];

  const togglePillar = (pillar: string) => {
    if (selectedPillars.includes(pillar)) {
      if (selectedPillars.length > 1) {
        setSelectedPillars(selectedPillars.filter((p) => p !== pillar));
      }
    } else {
      setSelectedPillars([...selectedPillars, pillar]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await submitWeb3Form('calculator', {
        fullName: formData.fullName,
        email: formData.email,
        selected_pillars: selectedPillars.join(', '),
        target_timeline: timeline,
        subject: `Instant RFP Estimate from ${formData.fullName}`,
      });
    } catch (err) {
      console.log('Web3Forms submission handled:', err);
    } finally {
      setIsSubmitting(false);
      setSubmitted(true);
    }
  };

  return (
    <section className="py-14 sm:py-16 lg:py-20 relative bg-white dark:bg-[#080e1a] transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Card variant="glass" className="p-6 sm:p-10 lg:p-12 bg-[#f5f9fc] dark:bg-[#0f1b2d] border border-slate-200 dark:border-[#4a82c2]/30 shadow-2xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left Scope Builder */}
            <div className="lg:col-span-7 space-y-6">
              <Badge variant="brand" className="px-3.5 py-1 text-[11px] sm:text-xs uppercase tracking-wider">
                <Calculator size={14} className="text-[#4a82c2]" /> Interactive RFP Scope Calculator
              </Badge>

              <h3 className="text-2xl sm:text-3xl font-extrabold text-[#184166] dark:text-white">
                Estimate your custom project scope in seconds
              </h3>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                Select your desired service pillars and target delivery timeline to build a customized RFP estimate for our engineering leadership in Parañaque.
              </p>

              {/* Pillars Selector */}
              <div className="space-y-2">
                <span className="text-xs font-bold text-[#184166] dark:text-slate-300 uppercase tracking-wider block">1. Select Core Pillars:</span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {pillarsList.map((p) => {
                    const isSelected = selectedPillars.includes(p);
                    return (
                      <button
                        type="button"
                        key={p}
                        onClick={() => togglePillar(p)}
                        className={`p-3 rounded-xl border text-xs text-left font-semibold transition-all flex items-center justify-between ${
                          isSelected
                            ? 'bg-sky-50 dark:bg-[#182a45] border-[#4a82c2] text-[#4a82c2] dark:text-sky-300 shadow-md'
                            : 'bg-white dark:bg-[#09101d] border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-400 hover:border-slate-300'
                        }`}
                      >
                        <span>{p}</span>
                        {isSelected && <Check size={16} className="text-[#4a82c2] shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Timeline Selector */}
              <div className="space-y-2 pt-2">
                <span className="text-xs font-bold text-[#184166] dark:text-slate-300 uppercase tracking-wider block">2. Delivery Timeline:</span>
                <div className="flex flex-wrap gap-2">
                  {timelines.map((t) => (
                    <button
                      type="button"
                      key={t}
                      onClick={() => setTimeline(t)}
                      className={`px-3.5 py-2 rounded-xl border text-xs font-medium transition-all ${
                        timeline === t
                          ? 'bg-[#08adae]/10 border-[#08adae] text-[#08adae]'
                          : 'bg-white dark:bg-[#09101d] border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400'
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Form & Instant RFP Summary */}
            <div className="lg:col-span-5 bg-white dark:bg-[#09101d] p-6 sm:p-8 rounded-3xl border border-slate-200 dark:border-slate-800 space-y-4 shadow-lg">
              <div className="border-b border-slate-200 dark:border-slate-800 pb-3 flex items-center justify-between">
                <span className="text-xs font-bold text-[#184166] dark:text-white uppercase tracking-wider">RFP Scope Summary</span>
                <Sparkles size={16} className="text-[#08adae]" />
              </div>

              <div className="space-y-2 text-xs">
                <div className="flex justify-between text-slate-600 dark:text-slate-400">
                  <span>Selected Pillars:</span>
                  <span className="font-bold text-[#4a82c2]">{selectedPillars.length} Active</span>
                </div>
                <div className="flex justify-between text-slate-600 dark:text-slate-400">
                  <span>Delivery Model:</span>
                  <span className="font-bold text-[#08adae]">{timeline}</span>
                </div>
              </div>

              {submitted ? (
                <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-600 dark:text-emerald-400 text-center space-y-2 py-6">
                  <Check className="mx-auto" size={28} />
                  <p className="text-xs font-bold">RFP Estimate Submitted!</p>
                  <p className="text-[11px] text-slate-600 dark:text-slate-300">Our Parañaque tech team will contact your email with a detailed scope proposal.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-3 pt-2">
                  <Input
                    label="Your Email Address"
                    placeholder="name@company.com"
                    required
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                  <Input
                    label="Full Name"
                    placeholder="e.g. Juan Cruz"
                    required
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  />
                  <Button type="submit" variant="primary" className="w-full" disabled={isSubmitting}>
                    {isSubmitting ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                    <span>{isSubmitting ? 'Submitting Scope...' : 'Submit Instant RFP Estimate'}</span>
                  </Button>
                </form>
              )}
            </div>
          </div>
        </Card>
      </div>
    </section>
  );
};
